import { getPsychologistId } from '../hooks/api/auth/tokenManager';
import { isPushSessionRestricted } from './foreignSession';
import {
  fetchPushStatus,
  registerPushDevice,
  type PushDeviceInfo,
  type PushPlatform,
  type PushStatus,
} from '../hooks/api/pushNotifications';
import { approximateAppleModel, cleanModel, normalizeVersion, parseBrowser, type HighEntropyValues } from './deviceInfo';
import { getFirebaseSettings } from './firebaseConfig';
import { isMobileDeviceNow, isPhoneDeviceNow } from './isMobileDevice';
import { isStandaloneDisplay } from './isPwaMode';

/**
 * Piezas compartidas por la pantalla Notificaciones (`usePushNotifications`) y el bloqueo de la app (`pushGate`):
 * soporte del navegador, token FCM de este dispositivo, la "intención" guardada y la activación.
 */

/**
 * Recuerda QUÉ psicóloga activó las push en este dispositivo, para re-registrar solo si el token
 * rota. Guarda su id (no un simple "sí"): si otra psicóloga se loguea después en el mismo celular,
 * no se le registra nada sin que lo pida — tiene que tocar "Activar" ella.
 */
// OJO con el nombre: `clearSession()` (cerrar sesión, cambiar de cuenta, IMPERSONAR) borra toda clave de localStorage que
// empiece con `psi-`. Con esa clave la intención se perdía al impersonar y el celular dejaba de refrescar su registro (psima
// lo veía inactivo) y de re-registrarse solo si el token rotaba. Sobrevivir al cierre de sesión es seguro porque la intención
// guarda el id de QUIÉN activó: otra cuenta en el mismo celular no coincide y no se le registra nada.
const INTENT_KEY = 'pwa-push-intent';
/** Nombre anterior (se perdía al cerrar sesión). Se lee una vez para no pedirle a nadie que active de nuevo y se migra. */
const LEGACY_INTENT_KEY = 'psi-push-intent';

export function readIntent(): string | null {
  try {
    const current = localStorage.getItem(INTENT_KEY);
    if (current) return current;
    const legacy = localStorage.getItem(LEGACY_INTENT_KEY);
    if (legacy) {
      localStorage.setItem(INTENT_KEY, legacy);
      localStorage.removeItem(LEGACY_INTENT_KEY);
    }
    return legacy;
  } catch {
    return null;
  }
}

export function writeIntent(psychologistId: string | null): void {
  try {
    localStorage.removeItem(LEGACY_INTENT_KEY);
    if (psychologistId) localStorage.setItem(INTENT_KEY, psychologistId);
    else localStorage.removeItem(INTENT_KEY);
  } catch {
    /* noop: sin storage solo se pierde la re-registración automática */
  }
}

/**
 * Con qué plataforma se registra este dispositivo en el backend. Un CELULAR es `android` o `ios`: cuenta como UN dispositivo por
 * plataforma (el token nuevo reemplaza al anterior) y es el que mira el ícono de psima. Una TABLET se registra como `desktop`: no
 * reemplaza al celular de la misma marca (iPhone + iPad, o dos Android) y no entra en ese ícono.
 */
export function detectPlatform(): PushPlatform {
  const ua = navigator.userAgent;
  if (isMobileDeviceNow() && !isPhoneDeviceNow()) return 'desktop';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  if (/Windows|Macintosh|Linux/i.test(ua)) return 'desktop';
  return 'other';
}

/** Sistema operativo del aparato, para explicar los pasos de los ajustes (independiente de cómo se registra: una tablet Android sigue siendo Android). */
export function detectOs(): 'ios' | 'android' | 'other' {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && (navigator.maxTouchPoints ?? 0) > 1)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
}

/**
 * Qué se sabe de este aparato, ya derivado (nunca el user agent crudo). Es informativo: NO cambia `detectPlatform` ni el
 * reemplazo de tokens. Nunca falla: lo que no se pueda saber se omite, y un error devuelve `undefined` (el registro sigue).
 *  - Android: el modelo exacto sale de `userAgentData` (solo Chromium); sin eso no se informa modelo.
 *  - iPhone/iPad: Safari no lo expone, se estima por pantalla (`modelApprox: true`). La versión de iOS 26 también es aproximada.
 */
export async function collectDeviceInfo(): Promise<PushDeviceInfo | undefined> {
  try {
    const ua = navigator.userAgent;
    const os = detectOs();
    const formFactor = isPhoneDeviceNow() ? 'phone' : isMobileDeviceNow() ? 'tablet' : 'desktop';
    const info: PushDeviceInfo = { formFactor, os, browser: parseBrowser(ua), installed: isStandaloneDisplay() };

    if (os === 'ios') {
      info.osVersion = normalizeVersion(/(?:iPhone OS|CPU OS|iPadOS) ([\d_]+)/i.exec(ua)?.[1]);
    } else if (os === 'android') {
      info.osVersion = normalizeVersion(/Android ([\d.]+)/i.exec(ua)?.[1]);
    }

    const w = window.screen?.width;
    const h = window.screen?.height;
    const dpr = window.devicePixelRatio;
    const hasScreen = w > 0 && h > 0 && dpr > 0;
    if (hasScreen) info.screen = { w: Math.round(w), h: Math.round(h), dpr: Math.round(dpr * 100) / 100 };

    if (os === 'ios' && info.screen && formFactor !== 'desktop') {
      info.model = approximateAppleModel(info.screen, formFactor);
      info.modelApprox = true;
    } else if (os === 'android') {
      const uaData = (navigator as unknown as { userAgentData?: { getHighEntropyValues?: (hints: string[]) => Promise<HighEntropyValues> } })
        .userAgentData;
      const values = await uaData?.getHighEntropyValues?.(['model', 'platformVersion']).catch(() => undefined);
      const model = cleanModel(values?.model);
      if (model) {
        info.model = model;
        info.modelApprox = false;
      }
      info.osVersion = normalizeVersion(values?.platformVersion) ?? info.osVersion;
    }
    return info;
  } catch {
    return undefined;
  }
}

/** Registra ESTE dispositivo (plataforma + info del aparato). Único punto por donde la app registra el token. */
export async function registerThisDevice(token: string): Promise<PushStatus> {
  return registerPushDevice(token, detectPlatform(), await collectDeviceInfo());
}

export function browserSupportsPush(): boolean {
  return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
}

/** De quién es este celular según las push: la psicóloga que las activó acá o, si no hay, la de la sesión actual. */
export function currentDeviceOwner(): string | null {
  return readIntent() ?? getPsychologistId();
}

/** Evento de la ventana: algo cambió el estado de las push en este dispositivo (activó / desactivó). El bloqueo lo escucha. */
export const PUSH_CHANGED_EVENT = 'psi-push-changed';

export function notifyPushChanged(): void {
  window.dispatchEvent(new Event(PUSH_CHANGED_EVENT));
}

/** Firebase se carga bajo demanda: pesa y solo lo necesita la PWA, no la plataforma web. */
export async function loadMessaging() {
  const [{ getApp, getApps, initializeApp }, messagingSdk] = await Promise.all([
    import('firebase/app'),
    import('firebase/messaging'),
  ]);
  const app = getApps().length > 0 ? getApp() : initializeApp(getFirebaseSettings().config);
  return { messaging: messagingSdk.getMessaging(app), sdk: messagingSdk };
}

/** Token FCM de este dispositivo, usando el service worker de la PWA (/sw.js) para recibir. */
export async function getDeviceToken(): Promise<string> {
  const registration = await navigator.serviceWorker.getRegistration('/');
  if (!registration) throw new Error('El service worker de la app todavía no está registrado.');
  await navigator.serviceWorker.ready;

  const { messaging, sdk } = await loadMessaging();
  const token = await sdk.getToken(messaging, {
    vapidKey: getFirebaseSettings().vapidKey,
    serviceWorkerRegistration: registration,
  });
  if (!token) throw new Error('No se pudo obtener el token de notificaciones.');
  return token;
}

export const IMPERSONATING_MESSAGE =
  'No se pueden activar las notificaciones mientras estás viendo la cuenta de otra persona (las del celular son de quien las activó).';

/**
 * Activa las push en este dispositivo: pide el permiso (tiene que ser LO PRIMERO que corre dentro del toque; iOS lo exige),
 * registra el token y guarda la intención. Devuelve `denied` si el permiso quedó bloqueado y `off` si no lo dio.
 * Nunca activa una sesión impersonada: registraría el celular del admin como el de la psicóloga.
 */
export async function activatePushOnDevice(): Promise<'on' | 'denied' | 'off'> {
  if (isPushSessionRestricted()) throw new Error(IMPERSONATING_MESSAGE);
  const permission = await Notification.requestPermission();
  if (permission === 'denied') return 'denied';
  if (permission !== 'granted') return 'off';

  const token = await getDeviceToken();
  await registerThisDevice(token);
  writeIntent(getPsychologistId());
  notifyPushChanged();
  return 'on';
}

export { fetchPushStatus };
